import React, { useEffect } from 'react';
import styles from './Activity.module.scss';
import { Stack } from 'new/elements/Stack';
import { Item } from 'new/elements/Activity/Item';
import { useAuditLogs } from 'new/hooks/adapters/useAuditLogs';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_AUDIT_LOG_REQUEST } from 'js/constants/actionTypes';
import { fetchAuditLog } from 'js/actions';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import { Button } from 'new/elements/Button';
import { Refresh } from '@mui/icons-material';
import { Skeleton } from '@mui/material';
import { Link } from 'react-router-dom';
import { EmptyState } from 'new/elements/EmptyState/EmptyState';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface ActivityProps {
  data: any;
  resourceType: string;
  resourceId: number;
  triedLoad?: boolean;
  preventRequest?: boolean;
}

export const Activity = (props: ActivityProps) => {
  const dispatch = useAppDispatch();
  const { data, resourceType, resourceId, preventRequest } = props;
  const items = useAuditLogs(resourceType, resourceId || data.id);
  const loading = useIsLoading(GET_AUDIT_LOG_REQUEST);

  useEffect(() => {
    if (!loading && (!items || !items.length) && resourceType && data.id && !preventRequest) {
      dispatch(fetchAuditLog(resourceType, data.id));
    }
  }, [data]);

  const onRefresh = () => {
    dispatch(fetchAuditLog(resourceType, resourceId || props.data?.id));
  };

  if (!loading && !items?.length) {
    if (props?.triedLoad) {
      return <NoContentTextView title="No activity found" />;
    }

    return (
      <EmptyState
        title="Find a Resource to Begin"
        description="Select a Resource Type and enter a Resource ID to review event activity for that resource."
        tip={
          <>
            Need help finding a Resource ID?{' '}
            <Link to="/integrate/flows" target="_blank">
              Check your Data Flows
            </Link>{' '}
            (opens in new tab) or ask your admin.
          </>
        }
      />
    );
  }

  if (loading) {
    return (
      <Stack pt="24px" className={styles.activityItems}>
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <div />
          <Skeleton variant="rounded" height="30px" width="90px" />
        </Stack>
        {new Array(5).fill(0).map((_, idx) => (
          <Item loading={loading} item={{} as any} resourceData={{}} key={`activity-${idx}`} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack pt="24px" className={styles.activityItems}>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <div />
        <Button onClick={onRefresh} startIcon={<Refresh />} color="primary">
          Refresh
        </Button>
      </Stack>
      {items.map((item) => (
        <Item key={item.id} item={item} resourceData={data} />
      ))}
    </Stack>
  );
};
